import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants, fetchTenantConfigFromDB, savePortfolioLevels, saveFundAllocations, saveAvailableFunds, saveDefaultFunds, fetchAllFunds, fetchProviders } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const ASSET_COLORS = { Equity: '#2563eb', Bond: '#f59e0b', Cash: '#10b981', Mixed: '#8b5cf6' };

function Toast({ toast }) {
    if (!toast) return null;
    const isError = toast.type === 'error';
    return (
        <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            background: isError ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${isError ? '#fca5a5' : '#86efac'}`,
            borderRadius: 10, padding: '12px 20px',
            color: isError ? '#991b1b' : '#166534',
            fontSize: 13, fontWeight: 500,
            boxShadow: '0 4px 20px rgba(0,0,0,.1)',
        }}>
            {isError ? '✕ ' : '✓ '}{toast.msg}
        </div>
    );
}

function PortfolioLevelsTab({ levels, onChange, onAdd, onRemove }) {
    const [adding, setAdding] = useState(false);
    const [newRow, setNewRow] = useState({ key: '', name: '', description: '' });

    const entries = Object.entries(levels || {}).sort(([a], [b]) => parseInt(a) - parseInt(b));

    const commitAdd = () => {
        const k = String(parseInt(newRow.key));
        if (!newRow.key || isNaN(parseInt(newRow.key)) || !newRow.name.trim()) return;
        onAdd(k, {
            name: newRow.name.trim(),
            description: newRow.description.trim() || null,
        });
        setNewRow({ key: '', name: '', description: '' });
        setAdding(false);
    };

    return (
        <div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Named risk tiers. Each level's fund split is set on the Allocation Matrix tab.
                Removing a level also removes its fund allocations.
            </p>
            <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm" style={{ fontSize: 12, minWidth: 480 }}>
                    <thead className="table-light">
                        <tr>
                            <th className="px-3" style={{ minWidth: 60 }}>Level</th>
                            <th className="px-3" style={{ minWidth: 160 }}>Name</th>
                            <th className="px-3" style={{ minWidth: 240 }}>Description</th>
                            <th style={{ width: 36 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(([key, data]) => (
                            <tr key={key}>
                                <td className="px-3 fw-semibold" style={{ color: '#0d2137', verticalAlign: 'middle' }}>{key}</td>
                                <td className="px-1 py-1">
                                    <input type="text" value={data.name || ''}
                                        onChange={e => onChange(key, 'name', e.target.value)}
                                        className="form-control form-control-sm" style={{ fontSize: 12 }} />
                                </td>
                                <td className="px-1 py-1">
                                    <input type="text" value={data.description || ''}
                                        onChange={e => onChange(key, 'description', e.target.value)}
                                        className="form-control form-control-sm" style={{ fontSize: 12 }} />
                                </td>
                                <td className="px-1 py-1 text-center">
                                    <button onClick={() => onRemove(key)}
                                        className="btn btn-sm btn-outline-danger"
                                        style={{ fontSize: 11, padding: '2px 7px', lineHeight: 1 }}
                                        title="Remove level">×</button>
                                </td>
                            </tr>
                        ))}
                        {adding && (
                            <tr style={{ background: '#f8faff' }}>
                                <td className="px-1 py-1">
                                    <input type="number" min="1" placeholder="#"
                                        value={newRow.key}
                                        onChange={e => setNewRow(p => ({ ...p, key: e.target.value }))}
                                        className="form-control form-control-sm text-center"
                                        style={{ fontFamily: 'monospace', fontSize: 12 }} />
                                </td>
                                <td className="px-1 py-1">
                                    <input type="text" placeholder="Name"
                                        value={newRow.name}
                                        onChange={e => setNewRow(p => ({ ...p, name: e.target.value }))}
                                        className="form-control form-control-sm" style={{ fontSize: 12 }} />
                                </td>
                                <td className="px-1 py-1">
                                    <input type="text" placeholder="Description (optional)"
                                        value={newRow.description}
                                        onChange={e => setNewRow(p => ({ ...p, description: e.target.value }))}
                                        className="form-control form-control-sm" style={{ fontSize: 12 }} />
                                </td>
                                <td className="px-1 py-1 text-center">
                                    <button onClick={commitAdd}
                                        className="btn btn-sm btn-primary"
                                        style={{ fontSize: 11, padding: '2px 8px', lineHeight: 1 }}
                                        disabled={!newRow.key || !newRow.name.trim()}>✓</button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!adding
                ? <button onClick={() => setAdding(true)} className="btn btn-sm btn-outline-primary" style={{ fontSize: 12 }}>+ Add Level</button>
                : <button onClick={() => setAdding(false)} className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }}>Cancel</button>
            }
        </div>
    );
}

function AllocationMatrixTab({ levels, matrix, funds, onChange }) {
    // Hooks must run before any early return (rules of hooks).
    const [sort, setSort] = useState('name-asc');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);

    if (!funds.length) return <p className="text-muted">No funds available for this tenant. Select funds on the Available Funds tab.</p>;
    if (!levels.length) return <p className="text-muted">No portfolio levels defined. Add levels on the Portfolio Levels tab.</p>;

    // Funds are the ROWS and portfolio levels the COLUMNS: a tenant may hold hundreds of funds but only a
    // handful of levels, so the (few) levels go across and the (many) funds down. The header (level names +
    // live per-level sum) and the fund-name column are sticky, so they stay fixed while the fund rows scroll.
    const HEAD_BG = '#f1f5f9';
    // The sum is over ALL funds (not just the visible page) — a level must total 100% across the whole set.
    const colSum = (level) => funds.reduce((a, f) => a + (parseFloat(matrix[level]?.[f.id]) || 0), 0);

    const nameOf = (f) => (f.short_name || f.name || '').toLowerCase();
    const sorted = [...funds].sort((a, b) => {
        switch (sort) {
            case 'name-desc': return nameOf(b).localeCompare(nameOf(a));
            case 'srri-asc':  return (a.srri ?? 0) - (b.srri ?? 0) || nameOf(a).localeCompare(nameOf(b));
            case 'srri-desc': return (b.srri ?? 0) - (a.srri ?? 0) || nameOf(a).localeCompare(nameOf(b));
            case 'name-asc':
            default:          return nameOf(a).localeCompare(nameOf(b));
        }
    });

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    const pageFunds = sorted.slice(start, start + pageSize);

    return (
        <div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Percentage allocation of each fund within each portfolio level. Each level (column) must sum
                to 100% — the live total is shown under each level heading.
            </p>

            {/* Sort + page size — just above the table header */}
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{ fontSize: 13 }}>
                <span className="text-muted">Sort</span>
                <Form.Select size="sm" value={sort} data-testid="matrix-sort"
                    onChange={e => { setSort(e.target.value); setPage(1); }} style={{ width: 170 }}>
                    <option value="name-asc">Name (A–Z)</option>
                    <option value="name-desc">Name (Z–A)</option>
                    <option value="srri-asc">SRRI (low→high)</option>
                    <option value="srri-desc">SRRI (high→low)</option>
                </Form.Select>
                <span className="text-muted ms-2">Show</span>
                <Form.Select size="sm" value={pageSize} data-testid="matrix-page-size"
                    onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }} style={{ width: 80 }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                </Form.Select>
                <span className="text-muted">funds</span>
            </div>

            <div style={{ overflow: 'auto', maxHeight: '65vh' }}>
                <table className="table table-sm" style={{ fontSize: 12, width: 'auto' }}>
                    <thead>
                        <tr>
                            <th className="px-3" style={{ minWidth: 200, position: 'sticky', top: 0, left: 0, zIndex: 3, background: HEAD_BG }}>Fund</th>
                            {levels.map(lvl => {
                                const s = colSum(lvl.key);
                                const ok = Math.abs(s - 100) < 0.5;
                                return (
                                    <th key={lvl.key} className="px-2 text-center" style={{ minWidth: 100, position: 'sticky', top: 0, zIndex: 2, background: HEAD_BG }}>
                                        <div style={{ fontWeight: 600, color: '#0d2137' }} title={`Level ${lvl.key}`}>{lvl.name || `Level ${lvl.key}`}</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: ok ? '#166534' : '#991b1b' }}>{s.toFixed(2)}%</div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                    {pageFunds.map(f => (
                        <tr key={f.id}>
                            <td className="px-3 fw-semibold" style={{ position: 'sticky', left: 0, zIndex: 1, background: '#fff', verticalAlign: 'middle' }}>
                                <div style={{ color: ASSET_COLORS[f.asset_class] || '#0d2137' }}>{f.short_name || f.name}</div>
                                <div style={{ fontSize: 10, color: '#94a3b8' }}>SRRI {f.srri}</div>
                            </td>
                            {levels.map(lvl => (
                                <td key={lvl.key} className="px-1 py-1">
                                    <input
                                        type="number"
                                        min="0" max="100" step="0.01"
                                        value={matrix[lvl.key]?.[f.id] ?? 0}
                                        onChange={e => onChange(lvl.key, f.id, e.target.value)}
                                        className="form-control form-control-sm text-end"
                                        style={{ fontFamily: 'monospace', fontSize: 12, padding: '3px 6px', minWidth: 72 }}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination — just below the table */}
            <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2" style={{ fontSize: 13 }}>
                <span className="text-muted">
                    Showing {start + 1}–{Math.min(start + pageSize, total)} of {total} funds
                </span>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-secondary" size="sm" disabled={safePage <= 1}
                        onClick={() => setPage(Math.max(1, safePage - 1))}>Previous</Button>
                    <span className="text-muted">Page {safePage} of {totalPages}</span>
                    <Button variant="outline-secondary" size="sm" disabled={safePage >= totalPages}
                        onClick={() => setPage(Math.min(totalPages, safePage + 1))}>Next</Button>
                </div>
            </div>
        </div>
    );
}

function AvailableFundsTab({ allFunds, availableFundIds, defaultFundIds, providerMap, onChange, onToggleDefault }) {
    const grouped = {};
    allFunds.forEach(f => {
        const key = f.provider_id || 'Other';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(f);
    });
    // Group header = the provider's NAME (fall back to its code, then the raw key) — never the provider_id.
    const providerLabel = (key) =>
        (providerMap && providerMap[key] && (providerMap[key].name || providerMap[key].code))
        || (key === 'Other' ? 'Other' : key);

    return (
        <div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Select which funds are available to this tenant. Only selected funds appear in the Allocation Matrix.
                Mark one or more as <strong>Default</strong> — optional, and any number (a trust-based scheme
                typically has one, a contract-based scheme several).
            </p>
            {Object.entries(grouped).map(([provider, funds]) => (
                <div key={provider} className="mb-4">
                    <div className="fw-semibold mb-2" style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {providerLabel(provider)}
                    </div>
                    <div className="row g-2">
                        {funds.map(f => {
                            const isAvailable = availableFundIds.includes(f.id);
                            const isDefault = defaultFundIds.includes(f.id);
                            return (
                                <div key={f.id} className="col-md-6 col-lg-4">
                                    <div className="d-flex align-items-center gap-2 p-2 rounded border"
                                        style={{ fontSize: 12, background: isAvailable ? '#f0f7ff' : '#fff' }}>
                                        <input type="checkbox" checked={isAvailable}
                                            onChange={() => onChange(f.id)}
                                            style={{ cursor: 'pointer' }}
                                            title={isAvailable ? 'Available to this tenant' : 'Make available to this tenant'} />
                                        <div className="flex-grow-1">
                                            <div className="fw-semibold" style={{ color: ASSET_COLORS[f.asset_class] || '#0d2137' }}>
                                                {f.short_name || f.name || f.id}
                                            </div>
                                            <div style={{ color: '#94a3b8', fontSize: 11 }}>SRRI {f.srri} · {f.asset_class}</div>
                                        </div>
                                        {isAvailable && (
                                            <Form.Check type="switch" id={`default-${f.id}`} label="Default"
                                                checked={isDefault} onChange={() => onToggleDefault(f.id)}
                                                title="Mark as a default fund for this tenant"
                                                style={{ fontSize: 11 }} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            {!allFunds.length && <p className="text-muted">No funds in catalogue.</p>}
            <p className="text-muted mt-1 mb-0" style={{ fontSize: 12 }}>
                {availableFundIds.length} of {allFunds.length} funds selected · {defaultFundIds.length} default
            </p>
        </div>
    );
}

function ProviderConfig({ tenantCode }) {
    const { can } = useAuth();
    const [tenantOptions, setTenantOptions] = useState([]);
    const [selectedCode, setSelectedCode] = useState(tenantCode || '');
    const [config, setConfig] = useState(null);
    const [allFunds, setAllFunds] = useState([]);
    const [providerMap, setProviderMap] = useState({});
    const [matrix, setMatrix] = useState({});
    const [portfolioLevels, setPortfolioLevels] = useState({});
    const [availableFundIds, setAvailableFundIds] = useState([]);
    const [defaultFundIds, setDefaultFundIds] = useState([]);
    const [activeTab, setActiveTab] = useState('levels');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    const tenantFunds = allFunds.filter(f => availableFundIds.includes(f.id));

    const showToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // Load tenant list and all funds on mount
    useEffect(() => {
        Promise.all([fetchTenants(), fetchAllFunds(), fetchProviders()])
            .then(([t, f, p]) => {
                // List ALL tenants, not only those already configured — otherwise a newly-created
                // tenant can never be configured (it isn't in the list until it has config).
                const options = (t.tenants || [])
                    .filter(x => x.active !== false)
                    .map(x => ({ code: x.code, name: x.name }));
                setTenantOptions(options);
                setAllFunds(f);
                const pm = {};
                (p?.providers || []).forEach(pr => { pm[pr.id] = pr; });
                setProviderMap(pm);
                // Embedded in the Tenants detail view the tenant is fixed via the prop (no picker).
                if (tenantCode) setSelectedCode(tenantCode);
                else if (options.length) setSelectedCode(options[0].code);
            })
            .catch(() => setError('Failed to load configuration.'))
            .finally(() => setLoading(false));
    }, [tenantCode]);

    // Load config when tenant changes — read directly from DB for accuracy
    useEffect(() => {
        if (!selectedCode) return;
        setDirty(false);
        fetchTenantConfigFromDB(selectedCode).then(data => {
            setConfig(data);
            setMatrix(data.allocation_matrix || {});
            setPortfolioLevels(data.portfolio_levels || {});
            setAvailableFundIds(data.available_funds || []);
            setDefaultFundIds(data.default_funds || []);
        }).catch(() => setError(`Failed to load config for ${selectedCode}.`));
    }, [selectedCode, allFunds]);

    const handleMatrixChange = (level, fundId, value) => {
        setMatrix(prev => ({ ...prev, [level]: { ...prev[level], [fundId]: parseFloat(value) || 0 } }));
        setDirty(true);
    };

    const handleFundToggle = (fundId) => {
        setAvailableFundIds(prev => {
            const removing = prev.includes(fundId);
            // a default must stay a subset of available — drop it from defaults when it's removed
            if (removing) setDefaultFundIds(d => d.filter(id => id !== fundId));
            return removing ? prev.filter(id => id !== fundId) : [...prev, fundId];
        });
        setDirty(true);
    };

    // Mark/unmark an available fund as a default (optional, any number — KAN-174).
    const handleDefaultToggle = (fundId) => {
        setDefaultFundIds(prev =>
            prev.includes(fundId) ? prev.filter(id => id !== fundId) : [...prev, fundId]
        );
        setDirty(true);
    };

    const handleLevelChange = (levelKey, field, value) => {
        setPortfolioLevels(prev => ({ ...prev, [levelKey]: { ...prev[levelKey], [field]: value } }));
        setDirty(true);
    };

    const handleLevelAdd = (levelKey, data) => {
        setPortfolioLevels(prev => ({ ...prev, [levelKey]: data }));
        setDirty(true);
    };

    const handleLevelRemove = (levelKey) => {
        setPortfolioLevels(prev => { const n = { ...prev }; delete n[levelKey]; return n; });
        setMatrix(prev => { const n = { ...prev }; delete n[levelKey]; return n; });
        setDirty(true);
    };

    const validate = () => {
        // A portfolio level must have a name — it's required at creation and labels the Allocation Matrix
        // columns / the Scoring page, so it can't be left (or cleared to) blank.
        for (const [lvl, data] of Object.entries(portfolioLevels)) {
            if (!(data.name || '').trim()) {
                showToast(`Portfolio level ${lvl} needs a name`, 'error');
                return false;
            }
        }
        for (const lvl of Object.keys(portfolioLevels)) {
            const row = matrix[lvl] || {};
            const s = availableFundIds.reduce((a, fid) => a + (parseFloat(row[fid]) || 0), 0);
            if (Math.abs(s - 100) > 0.5) {
                showToast(`Allocation level ${lvl} sums to ${s.toFixed(2)}% — must be 100%`, 'error');
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            // Persist the full level × available-fund grid: every portfolio level gets a row and every
            // available fund a column, so each intersection exists (an untouched cell is a real 0%),
            // not just the cells that were edited.
            const fullMatrix = {};
            for (const lvl of Object.keys(portfolioLevels)) {
                fullMatrix[lvl] = {};
                for (const fid of availableFundIds) {
                    fullMatrix[lvl][fid] = parseFloat(matrix[lvl]?.[fid]) || 0;
                }
            }
            // tenant_portfolio_levels.target_equity/bond/cash are NOT NULL in the DB but surplus to the
            // engine — the real asset split comes from the Allocation Matrix. The UI no longer collects
            // them; send 0 (preserving any legacy value) so the write stays valid. Dropping the columns
            // is a separate backend cleanup.
            const levelsForSave = Object.fromEntries(
                Object.entries(portfolioLevels).map(([k, v]) => [k, {
                    ...v,
                    target_equity: v.target_equity ?? 0,
                    target_bond: v.target_bond ?? 0,
                    target_cash: v.target_cash ?? 0,
                }])
            );
            // Sequential: levels must exist before allocations reference them; available funds must be
            // written before defaults (a default is validated to be one of the available funds).
            await savePortfolioLevels(selectedCode, levelsForSave);
            await saveAvailableFunds(selectedCode, availableFundIds);
            await saveDefaultFunds(selectedCode, defaultFundIds);
            await saveFundAllocations(selectedCode, fullMatrix);
            setDirty(false);
            showToast('Configuration saved');
        } catch (e) {
            showToast(e.response?.data?.detail || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const TABS = [
        { id: 'levels',  label: 'Portfolio Levels' },
        { id: 'matrix',  label: 'Allocation Matrix' },
        { id: 'funds',   label: 'Available Funds' },
    ];

    if (loading) {
        return (
            <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Configuration</h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Manage portfolio levels, the allocation matrix, and available funds per tenant. Risk scoring and the SRRI-to-portfolio mapping live on the Scoring page.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => {
                        setSelectedCode('');
                        setTimeout(() => setSelectedCode(selectedCode), 10);
                    }}>
                        <FontAwesomeIcon icon="rotate-right" className="me-1" /> Reload
                    </Button>
                    <Button variant={dirty ? 'primary' : 'outline-secondary'} size="sm"
                        onClick={handleSave} disabled={!dirty || saving || !can.editCatalogue()}
                        title={can.editCatalogue() ? undefined : "Requires the waive-config role"}>
                        {saving && <Spinner size="sm" animation="border" className="me-1" />}
                        {dirty ? 'Save Changes' : 'Saved'}
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}

            {/* Tenant selector */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="py-3 d-flex align-items-center gap-3">
                    {!tenantCode && (
                        <>
                            <label className="fw-semibold text-nowrap" style={{ fontSize: 13 }}>Tenant</label>
                            <Form.Select size="sm" value={selectedCode} onChange={e => setSelectedCode(e.target.value)}
                                style={{ maxWidth: 320 }}>
                                {tenantOptions.map(t => (
                                    <option key={t.code} value={t.code}>{t.code} — {t.name}</option>
                                ))}
                            </Form.Select>
                        </>
                    )}
                    {config && (
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            {availableFundIds.length} funds · {Object.keys(portfolioLevels).length} portfolio levels
                        </span>
                    )}
                </Card.Body>
            </Card>

            {config && (
                <Card className="border-0 shadow-sm">
                    {/* Tab bar */}
                    <Card.Header className="bg-white border-bottom d-flex gap-1 py-0">
                        {TABS.map(tab => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className="border-0 bg-transparent px-4 py-3"
                                style={{
                                    fontSize: 13, fontWeight: 500,
                                    color: activeTab === tab.id ? '#0d2137' : '#64748b',
                                    borderBottom: activeTab === tab.id ? '2px solid #4da6ff' : '2px solid transparent',
                                    cursor: 'pointer',
                                }}>
                                {tab.label}
                            </button>
                        ))}
                    </Card.Header>
                    <Card.Body className="p-4">
                        {activeTab === 'levels' && (
                            <PortfolioLevelsTab
                                levels={portfolioLevels}
                                onChange={handleLevelChange}
                                onAdd={handleLevelAdd}
                                onRemove={handleLevelRemove}
                            />
                        )}
                        {activeTab === 'matrix' && (
                            <AllocationMatrixTab
                                levels={Object.keys(portfolioLevels)
                                    .sort((a, b) => parseInt(a) - parseInt(b))
                                    .map(k => ({ key: k, name: portfolioLevels[k]?.name }))}
                                matrix={matrix}
                                funds={tenantFunds}
                                onChange={handleMatrixChange}
                            />
                        )}
                        {activeTab === 'funds' && (
                            <AvailableFundsTab
                                allFunds={allFunds}
                                availableFundIds={availableFundIds}
                                defaultFundIds={defaultFundIds}
                                providerMap={providerMap}
                                onChange={handleFundToggle}
                                onToggleDefault={handleDefaultToggle}
                            />
                        )}
                    </Card.Body>
                </Card>
            )}

            <Toast toast={toast} />
        </div>
    );
}

export default ProviderConfig;
